import type { CartItem } from '@/features/cart/cart.store';

/**
 * Передача собранной корзины в мини-приложение телеграма.
 *
 * Оформление живёт там: адрес, оплата, история заказов — всё это уже есть
 * в боте, и собирать те же поля второй раз на сайте значит держать две
 * формы, которые разойдутся на первой же правке.
 *
 * Состав заказа уходит **не через адресную строку**. Телеграм отдаёт
 * мини-приложению один параметр `startapp` длиной 64 знака — корзина
 * из четырёх позиций туда не влезает, а в ссылке она к тому же видна
 * любому, кто её перешлёт. Поэтому корзина откладывается на сервере,
 * а наружу уходит короткий ключ.
 *
 * Сервер — а не хранилище вкладки — потому что человек уходит в телеграм,
 * а телеграм на телефоне открывает приложение в своём браузере. Хранилище
 * вкладки туда не попадает, и корзина, собранная на компьютере, в телефоне
 * бы просто не нашлась.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';

interface DraftResponse {
  token: string;
  total_rub: number;
  miniapp_url: string;
}

/** Корзина в том виде, в каком её принимает сервер. */
function toPayload(items: CartItem[]) {
  return {
    items: items.map((item) => ({
      product_id: item.productId,
      title: item.title,
      color: item.color,
      size: item.size,
      qty: item.qty,
      unit_price_rub: item.unitPriceRub,
      print_url: item.printUrl ?? null,
      preview_url: item.previewUrl ?? null,
      print_side: item.printSide ?? 'front',
      is_custom: item.isCustom ?? false,
    })),
  };
}

/**
 * Отложить корзину и получить ссылку в мини-приложение.
 *
 * Возвращает `null`, если сервер не ответил: тогда человека всё равно
 * уводим в бот, просто без состава — там он соберёт заново. Потерять
 * покупателя на ошибке сети хуже, чем потерять его корзину.
 */
export async function saveDraft(
  partnerSlug: string,
  items: CartItem[],
): Promise<DraftResponse | null> {
  try {
    const response = await fetch(`${BASE}/api/v1/partners/${partnerSlug}/drafts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(toPayload(items)),
    });

    if (!response.ok) return null;

    return (await response.json()) as DraftResponse;
  } catch {
    return null;
  }
}

/**
 * Увести человека в мини-приложение вместе с корзиной.
 *
 * Новой вкладкой, а не заменой текущей: виджет стоит блоком на странице
 * заказчика, и уводить с неё человека целиком — значит закрыть ему
 * страницу, на которую он пришёл.
 */
export async function goToMiniapp(
  partnerSlug: string,
  fallbackUrl: string | undefined,
  items: CartItem[],
): Promise<string | null> {
  // Вкладка открывается сразу, в том же нажатии, а адрес ей даётся потом.
  //
  // Раньше вкладка открывалась после сохранения корзины — то есть после
  // сетевого запроса, — и браузер уже не считал это ответом на нажатие.
  // Мобильный Safari и встроенные в чужую страницу блоки такие окна
  // молча блокируют: человек жал «Оплатить», и не происходило ничего.
  const tab = window.open('about:blank', '_blank');

  const draft = items.length > 0 ? await saveDraft(partnerSlug, items) : null;
  const url = draft?.miniapp_url ?? fallbackUrl ?? null;

  if (!url) {
    tab?.close();
    return null;
  }

  if (tab) {
    try {
      // Отрезаем вкладке ссылку на нас — то же, что делал noopener.
      tab.opener = null;
      tab.location.href = url;
    } catch {
      tab.close();
    }
  }

  // Адрес нужен экрану «спасибо»: там он стоит кнопкой на случай, если
  // браузер вкладку всё-таки не открыл.
  return url;
}
